import { isEmpty, isNil } from 'lodash'

import constants from 'constants/index'

import getCount from 'helpers/file/getCount'
import getMimeTypeFromExtension from 'helpers/file/getMimeTypeFromExtension'

const size = (unit, sizeLimit, files) => {
  if (isNil(files) || (files instanceof FileList && isEmpty(files))) return true

  const exponentiation = constants.validations.BYTE_UNIT_EXPONENT[unit]
  const checkFileSize = (file) => file?.size <= sizeLimit * exponentiation

  if (files instanceof FileList || Array.isArray(files)) {
    return Array.from(files)
      .filter((file) => file instanceof File)
      .every(checkFileSize)
  }

  return checkFileSize(files)
}

const type = (permittedTypes, files) => {
  if (isNil(files) || (files instanceof FileList && isEmpty(files))) return true

  const checkFileType = (file) => permittedTypes.includes(file?.type)

  if (files instanceof FileList || Array.isArray(files)) {
    return Array.from(files)
      .filter((file) => file instanceof File)
      .every(checkFileType)
  }

  return checkFileType(files)
}

const required = (files) => getCount(files) > 0

const uniqueFile = (value) => {
  if (!value || value.length === 0) return true

  const filesArray =
    value instanceof FileList || Array.isArray(value)
      ? Array.from(value)
      : [value]

  const fileNames = filesArray.map((file) => file?.name)
  const hasDuplicates = fileNames.some(
    (name, idx) => fileNames.indexOf(name) !== idx,
  )

  return !hasDuplicates
}

const isSameFile = (file1, file2) => {
  return file1?.name === file2?.name && file1?.size === file2?.size
}

const isFileInList = (file, fileList) => {
  const filesArray = fileList ? Array.from(fileList) : []

  return (
    Array.isArray(filesArray) &&
    filesArray?.some((apiFile) => isSameFile(file, apiFile))
  )
}

const isFileAccepted = (file, accept) => {
  const acceptedTypes = accept?.split(',')?.map((type) => type.trim()) || []

  if (!accept || acceptedTypes.length === 0) return true

  let fileMimeType = file.type

  if (
    !fileMimeType ||
    fileMimeType === constants.validations.UNKNOWN_FILE_MIME_TYPE
  ) {
    fileMimeType = getMimeTypeFromExtension(file.name)
  }

  return acceptedTypes.some((acceptedType) => {
    if (acceptedType.startsWith('.')) {
      return file.name.toLowerCase().endsWith(acceptedType.toLowerCase())
    }

    return fileMimeType === acceptedType
  })
}

const isFile = (value) => {
  return (
    value instanceof File ||
    value instanceof FileList ||
    (Array.isArray(value) && value.every((item) => item instanceof File))
  )
}

const defineIsPreview = (file) => {
  if (file instanceof FileList && file.length === 1) {
    file = file[0]
  }

  if (file instanceof File && file?.type)
    return constants.validations.OPENABLE_FILE_MIME_TYPES.includes(file.type)

  if (file?.name) {
    const extension = file.name.split('.').pop()?.toLowerCase()
    return constants.validations.OPENABLE_FILE_TYPES.includes(extension)
  }

  return false
}

export default {
  size,
  type,
  required,
  uniqueFile,
  isSameFile,
  isFileInList,
  isFileAccepted,
  isFile,
  defineIsPreview,
}
